/**
 * Maps a notification `type` to an icon + tinted tile color so each row in the
 * notification list is instantly scannable. Shared across all apps.
 *
 * The Tailwind color classes are written as full literal strings on purpose —
 * each consuming app scans `packages/ui/src` for utilities, so dynamically
 * composed class names would be purged.
 */
import {
  AlertTriangle,
  Bell,
  BellRing,
  Calendar,
  CalendarX,
  CheckCircle2,
  ClipboardList,
  Clock,
  Gift,
  MessageSquareQuote,
  Share2,
  Sparkles,
  Star,
  UserCheck,
  UserPlus,
  Video,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { NOTIFICATION_TYPES as T } from './notificationTypes';

export interface NotificationVisual {
  Icon: LucideIcon;
  /** Combined background + icon color utilities for the tile. */
  tile: string;
}

const MEETING = 'bg-indigo-50 text-indigo-600';
const REWARD = 'bg-emerald-50 text-emerald-600';
const PENDING = 'bg-amber-50 text-amber-600';
const REVIEW = 'bg-violet-50 text-violet-600';
const PEOPLE = 'bg-emerald-50 text-emerald-600';
const REFERRAL = 'bg-teal-50 text-teal-600';
const SURVEY = 'bg-sky-50 text-sky-600';
const DANGER = 'bg-rose-50 text-rose-600';
const LIFECYCLE = 'bg-fuchsia-50 text-fuchsia-600';

const VISUALS: Record<string, NotificationVisual> = {
  // Vendor lifecycle
  [T.VENDOR_FIRST_LOGIN]: { Icon: Sparkles, tile: LIFECYCLE },
  [T.VENDOR_ONBOARDING_COMPLETED]: { Icon: Sparkles, tile: LIFECYCLE },

  // Promoters / prospects
  [T.NEW_PROMOTER_VERIFIED]: { Icon: UserCheck, tile: PEOPLE },
  [T.NEW_PROSPECT_VERIFIED]: { Icon: UserCheck, tile: PEOPLE },
  [T.PROSPECT_FINALIZED]: { Icon: CheckCircle2, tile: PEOPLE },
  [T.PROMOTER_DEMO_REQUESTED]: { Icon: Video, tile: 'bg-blue-50 text-blue-600' },

  // Reviews / testimonials
  [T.REVIEW_PUBLISHED]: { Icon: Star, tile: REVIEW },
  [T.TESTIMONIAL_REQUESTED]: { Icon: MessageSquareQuote, tile: REVIEW },
  [T.TESTIMONIAL_SUBMITTED]: { Icon: MessageSquareQuote, tile: REVIEW },
  [T.TESTIMONIAL_APPROVED]: { Icon: MessageSquareQuote, tile: REVIEW },
  [T.TESTIMONIAL_REJECTED]: { Icon: MessageSquareQuote, tile: REVIEW },

  // Meetings
  [T.MEETING_SCHEDULED]: { Icon: Calendar, tile: MEETING },
  [T.MEETING_ACCEPTED]: { Icon: Calendar, tile: MEETING },
  [T.MEETING_RESCHEDULED]: { Icon: Calendar, tile: MEETING },
  [T.MEETING_STARTED]: { Icon: Video, tile: MEETING },
  [T.MEETING_ENDED]: { Icon: Calendar, tile: MEETING },
  [T.MEETING_COMING_SOON]: { Icon: Clock, tile: MEETING },
  [T.MEETING_PARTICIPANT_JOINED]: { Icon: Video, tile: MEETING },
  [T.MEETING_CANCELLED]: { Icon: CalendarX, tile: DANGER },

  // Referrals / surveys / funds
  [T.FUND_REQUEST_CREATED]: { Icon: Wallet, tile: PENDING },
  [T.REFERRAL_CREATED]: { Icon: Share2, tile: REFERRAL },
  [T.REFERRAL_ASSIGNED]: { Icon: UserPlus, tile: REFERRAL },
  [T.SURVEY_RESPONSE_RECEIVED]: { Icon: ClipboardList, tile: SURVEY },

  // Rewards
  [T.DISCOUNT_REWARD_ISSUED]: { Icon: Gift, tile: REWARD },
  [T.SPECIFIC_REWARD_CREATED]: { Icon: Gift, tile: REWARD },
  [T.SPECIFIC_REWARD_COMPLETED]: { Icon: Gift, tile: REWARD },
  [T.SPECIFIC_REWARD_CLAIMED]: { Icon: Gift, tile: REWARD },
  [T.SPECIFIC_REWARD_PAID]: { Icon: Gift, tile: REWARD },
  [T.SPECIFIC_REWARD_REMINDER]: { Icon: BellRing, tile: PENDING },
  [T.GIFT_CARD_REWARD_PENDING]: { Icon: Clock, tile: PENDING },
  [T.GIFT_CARD_REWARD_PAID]: { Icon: Gift, tile: REWARD },
  [T.GIFT_CARD_REWARD_FAILED]: { Icon: AlertTriangle, tile: DANGER },
  [T.REWARD_CHOICE_PENDING]: { Icon: Gift, tile: PENDING },
  [T.INSUFFICIENT_BALANCE]: { Icon: AlertTriangle, tile: DANGER },
};

const DEFAULT_VISUAL: NotificationVisual = { Icon: Bell, tile: 'bg-slate-100 text-slate-500' };

export function getNotificationVisual(type: string): NotificationVisual {
  return VISUALS[type] ?? DEFAULT_VISUAL;
}
